import type { ComponentApi } from "@a2ui/web_core/v0_9";
import { zodToJsonSchema } from "zod-to-json-schema";

// Verbatim copy of @a2ui/web_core's
// src/v0_9/schemas/common_types.json (the A2UI v0.9 common types), vendored so
// the document can carry them itself. The package does not export that path,
// and the backend refuses any $ref it has no registered copy of.
import commonTypes from "./commonTypes.json";

type JsonSchema = Record<string, unknown>;

// One catalog document per contract. The report catalog reuses this builder so
// both documents stay the same shape the backend validates.
export interface CatalogContract {
  catalogId: string;
  version: number;
  title: string;
  description: string;
  apis: ComponentApi[];
}

// A `$ref` is either a pointer inside this document or a pointer into a file
// the server would have to have registered. `FunctionCall` in common_types.json
// is relative to a sibling (`catalog.json#/$defs/anyFunction`), and our own
// markers point at `common_types.json`. Both have to become local: the
// document travels alone.
function localizePointer(pointer: string): string {
  return `#${pointer.split("#")[1] ?? ""}`;
}

// Rewrites every `$ref` that names another file into a pointer inside this one.
function localizeRefs(node: unknown): void {
  if (typeof node !== "object" || node === null) return;

  const schema = node as JsonSchema;
  if (typeof schema.$ref === "string" && !schema.$ref.startsWith("#")) {
    schema.$ref = localizePointer(schema.$ref);
  }

  for (const child of Object.values(schema)) localizeRefs(child);
}

// The a2ui schemas mark shared types with a `REF:<pointer>|<prose>` description
// instead of a real $ref, because Zod cannot express one. Swap each marked node
// for the $ref it stands for, rewritten to a pointer inside this document: the
// common types travel in `$defs` below, so nothing is left to look up.
function collapseRefs(node: unknown): void {
  if (typeof node !== "object" || node === null) return;

  const schema = node as JsonSchema;
  const description = schema.description;

  if (typeof description === "string" && description.startsWith("REF:")) {
    const [pointer, prose] = description.slice("REF:".length).split("|");
    for (const key of Object.keys(schema)) delete schema[key];
    schema.$ref = localizePointer(pointer);
    if (prose) schema.description = prose;
    return;
  }

  for (const child of Object.values(schema)) collapseRefs(child);
}

// Builds the catalog document handed to the backend, in the same shape as the
// a2ui reference catalogs (see @a2ui/web_core/src/v0_9/schemas/catalogs).
export function buildCatalogDocument(contract: CatalogContract) {
  const components: Record<string, JsonSchema> = {};

  for (const api of contract.apis) {
    // `$refStrategy: "none"` matters: by default a shared schema used twice in
    // one component becomes an internal pointer that dangles once the
    // properties move under `allOf`.
    const schema = zodToJsonSchema(api.schema, {
      target: "jsonSchema2019-09",
      $refStrategy: "none"
    }) as JsonSchema;
    collapseRefs(schema);

    components[api.name] = {
      type: "object",
      description: api.schema.description,
      allOf: [
        // Same document, not a URI: a server that has to look this up in its
        // own registry rejects the whole catalog when it cannot.
        { $ref: "#/$defs/ComponentCommon" },
        {
          type: "object",
          properties: {
            component: { const: api.name },
            ...(schema.properties as Record<string, JsonSchema>)
          },
          required: ["component", ...((schema.required as string[]) ?? [])]
        }
      ],
      // ComponentCommon covers `id` and `accessibility`, the branch above
      // covers `component` and the props, so anything else is a mistake.
      unevaluatedProperties: false
    };
  }

  const document = {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $id: contract.catalogId,
    title: contract.title,
    description: contract.description,
    catalogId: contract.catalogId,
    version: contract.version,
    components,
    $defs: {
      // The a2ui common types, inlined. Their own refs point at `#/$defs/...`
      // already; the one that names a sibling file is localized below.
      ...(commonTypes.$defs as Record<string, JsonSchema>),
      // `FunctionCall` closes over this: a call must name a function the
      // catalog declares. v1 declares none (Q6), so the only truthful schema is
      // one nothing satisfies — `not: {}` rather than an empty `oneOf`, which
      // validators reject outright. Enumerate `#/functions/<name>` here once the
      // catalog carries a `functions` block.
      anyFunction: {
        description:
          "This catalog declares no functions, so no function call validates.",
        not: {}
      },
      // Validate every component the agent writes against this.
      anyComponent: {
        oneOf: contract.apis.map((api) => ({
          $ref: `#/components/${api.name}`
        })),
        discriminator: { propertyName: "component" }
      }
    }
  };

  localizeRefs(document);

  return document;
}
